// Legacy EventCategory values + Plan 4J AppCategory values, both stored
// in the legacy `category` text column. New events created via the Plan 4J
// form persist their AppCategory verbatim here so display layers don't
// have to dual-read.
export type EventCategory =
  | 'workshop'
  | 'fundraiser'
  | 'social'
  | 'family-day'
  | 'volunteering'
  | 'Families'
  | 'Teens'
  | 'Angels'
  | 'Siblings'
  | 'Parents'
  | 'Fundraising';

// Plan 4J: structured location + AppCategory taxonomy that mirrors CRM's
// canonical enums (packages/shared/src/types/events.ts). Kept as string
// literal unions so the dropdowns stay typed end-to-end without pulling
// in the CRM shared package.
export const EVENT_LOCATION_KEYS = [
  'LivingstonDen',
  'AberdeenDen',
  'GlasgowPopUp',
  'FifePopUp',
  'Outing',
] as const;
export type EventLocationKey = typeof EVENT_LOCATION_KEYS[number];

export const EVENT_APP_CATEGORIES = [
  'Families',
  'Teens',
  'Angels',
  'Siblings',
  'Parents',
  'Fundraising',
] as const;
export type EventAppCategory = typeof EVENT_APP_CATEGORIES[number];

export interface EventLocationOverride {
  placeName: string;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  postcode?: string | null;
  lat?: number | null;
  lng?: number | null;
}

export interface Event {
  id: string;
  title: string;
  description: string;
  date: string;
  startTime: string;
  endTime: string;
  location: string;
  capacity: number;
  currentAttendees: number;
  imageUrl?: string;
  category: EventCategory;
  isActive: boolean;
  registrationDeadline: string;
  registrationDeadlineTime?: string;
  createdAt: string;
  updatedAt: string;
  crmId?: string;
  // Plan 4J structured location + AppCategory (set by CRM-sourced events
  // and by App-created events that go through the new LocationField).
  // Optional so rows created before Plan 4J still type-check.
  appCategory?: EventAppCategory | null;
  locationKey?: EventLocationKey | null;
  locationDisplayName?: string | null;
  locationAddressLine1?: string | null;
  locationAddressLine2?: string | null;
  locationCity?: string | null;
  locationPostcode?: string | null;
  locationLat?: number | null;
  locationLng?: number | null;
  locationMapUrl?: string | null;
  locationIsAdHoc?: boolean | null;
  // True for the online (Zoom) location. The join link is never on the event.
  locationIsOnline?: boolean;
}

export interface EventFormData {
  title: string;
  description: string;
  date: string;
  startTime: string;
  endTime: string;
  location: string;
  capacity: number;
  category: EventCategory;
  imageUrl?: string;
  registrationDeadline: string;
  registrationDeadlineTime?: string;
  isActive: boolean;
  // Plan 4J: structured fields. Sent to App Backend, which forwards to CRM
  // via crmEventsPusher (Task 4.7) when crmIntegrationEnabled is true.
  appCategory?: EventAppCategory | null;
  locationKey?: EventLocationKey | null;
  locationOverride?: EventLocationOverride | null;
  // Plan 4K Phase 2: CRM Centre GUID. App Backend persists + forwards.
  centreId?: string | null;
}

// Plan 4K Phase 2: Centre option for the CreateEventPage dropdown.
// App Backend's GET /centres returns this shape (proxy of CRM /centres).
export interface CentreOption {
  id: string;
  name: string;
  region: string | null;
}

// Plan 4J entries listed first so they win in filter dropdowns; legacy
// entries kept so historical events still render their original label.
export const EVENT_CATEGORIES: { value: EventCategory; label: string }[] = [
  { value: 'Families', label: 'Families' },
  { value: 'Teens', label: 'Teens' },
  { value: 'Angels', label: 'Angels' },
  { value: 'Siblings', label: 'Siblings' },
  { value: 'Parents', label: 'Parents' },
  { value: 'Fundraising', label: 'Fundraising' },
  { value: 'workshop', label: 'Workshop' },
  { value: 'fundraiser', label: 'Fundraiser' },
  { value: 'social', label: 'Social Event' },
  { value: 'family-day', label: 'Family Day' },
  { value: 'volunteering', label: 'Volunteering' },
];

const CATEGORY_COLORS: Record<EventCategory, string> = {
  // Plan 4J AppCategory palette
  Families: '#ff9800',     // warm orange
  Teens: '#9c27b0',        // purple
  Angels: '#607d8b',       // muted blue-grey (subdued, respectful)
  Siblings: '#4caf50',     // green
  Parents: '#2196f3',      // blue
  Fundraising: '#43a047',  // strong green
  // Legacy palette retained for historical events
  workshop: '#9c27b0',
  fundraiser: '#4caf50',
  social: '#2196f3',
  'family-day': '#ff9800',
  volunteering: '#e91e63',
};

export const getCategoryLabel = (category: EventCategory): string => {
  const found = EVENT_CATEGORIES.find((c) => c.value === category);
  return found ? found.label : category;
};

export const getCategoryColor = (category: EventCategory): string =>
  CATEGORY_COLORS[category] ?? '#00b7ed';
